import {useCallback, useEffect} from 'react';
import {getCurrentUrlWithAdditonalParam} from '../../../../common/getCurrentUrlWithAdditonalParam';
import {tt} from '../../../../common/getTranslations';
import {requestContext} from '../../../../common/requestContext';
import {TRIGGER_COMMAND_KEY} from '../../../../common/shared/constants';
import {
  COMMAND_KEY,
  runCommand,
  saveCommand,
} from '../../../../common/shared/hooks/useCommandWithLocalStorage';
import useTypedSelector from '../../../../common/useTypedSelector';
import {commonErrorHandler} from '../../../../errors/commonErrorHandler';
import {SaleState} from '../../../../graphql/generated/types';
import {ONLY_DATA_CONFIG} from '../../../../reactQuery/constant';
import {showToast} from '../../../../toast/toastHandler';
import {useLogin} from '../../../../user/useLogin.hook';
import {useContentHomeOverviewQuery} from '../../../../wrapper/contentWrapper';
import {useSetSeriesMutationHandler} from '../../../inven/[invenType]/hook/useSetSeriesMutationHandler';
import {generateRelayRedirectUrl} from '../../../relay/redirect/util/generateRelayRedirectUrl';
import {VIEWER_TOAST_MESSAGES} from '../viewer/[productId]/constant/viewerConstant';

export function useContentHomeLikeAndAlarm(seriesId: number) {
  const {isLogin, doLogin} = useLogin();
  const {data, refetch} = useContentHomeOverviewQuery(
    {seriesId},
    ONLY_DATA_CONFIG
  );
  const {onSetSeriesAlarmMutate, onSetSeriesLikeMutate} =
    useSetSeriesMutationHandler();

  const {isEnteredContentHome} = useTypedSelector(state => {
    if (!seriesId) {
      return {};
    }
    return {
      isEnteredContentHome: state.contentHome.isEnteredContentHome[seriesId],
    };
  });
  const isLikeOn =
    data?.contentHomeOverview?.content?.serviceProperty?.isLikeOn;
  const isAlarmOn =
    data?.contentHomeOverview?.content?.serviceProperty?.isAlarmOn;
  const saleState =
    data?.contentHomeOverview?.content?.saleState || SaleState.Unknown;

  const onClickLikeSeries = useCallback(
    (parameters?: {fromSavedCommand?: boolean}) => {
      if (!isLogin) {
        if (!parameters?.fromSavedCommand) {
          saveCommand({
            commandKey: COMMAND_KEY.CLICK_LIKE,
            command: {location: window.location.pathname},
          }).then(() =>
            doLogin(
              generateRelayRedirectUrl(
                getCurrentUrlWithAdditonalParam(
                  TRIGGER_COMMAND_KEY,
                  COMMAND_KEY.CLICK_LIKE
                )
              )
            )
          );
        }
        return;
      }
      const targetIsLinkOn = !isLikeOn;
      const message = targetIsLinkOn
        ? VIEWER_TOAST_MESSAGES.likeOn
        : VIEWER_TOAST_MESSAGES.likeOff;
      onSetSeriesLikeMutate(seriesId, targetIsLinkOn)
        .then(result => {
          if (result.setSeriesLike.status !== 'Success') {
            return;
          }
          refetch();
          showToast({
            message: tt(message),
            isMobile: requestContext.requestInfo.display.isMobile,
          });
        })
        .catch(error => {
          commonErrorHandler(error);
        });
    },
    [isLogin, isLikeOn, onSetSeriesLikeMutate, seriesId, doLogin, refetch]
  );
  useEffect(() => {
    if (!data) {
      return;
    }
    runCommand({
      commandKey: COMMAND_KEY.CLICK_LIKE,
      action: async () => {
        if (isLikeOn) {
          showToast({
            message: tt(VIEWER_TOAST_MESSAGES.likeAlready),
            isMobile: requestContext.requestInfo.display.isMobile,
          });
        } else {
          await onClickLikeSeries({fromSavedCommand: true});
        }
      },
    });
  }, [data, isLikeOn, onClickLikeSeries]);

  const onClickAlarmOnSeries = useCallback(() => {
    if (!isLogin) {
      doLogin();
      return;
    }
    const targetIsAlarmOn = !isAlarmOn;
    onSetSeriesAlarmMutate(Number(seriesId), !isAlarmOn)
      .then(result => {
        if (result.setSeriesAlarm.status !== 'Success') {
          return;
        }
        const message = targetIsAlarmOn
          ? VIEWER_TOAST_MESSAGES.alarmOn
          : VIEWER_TOAST_MESSAGES.alarmOff;

        refetch();
        showToast({
          message: tt(message),
          isMobile: requestContext.requestInfo.display.isMobile,
        });
      })
      .catch(error => {
        commonErrorHandler(error);
      });
  }, [isLogin, isAlarmOn, onSetSeriesAlarmMutate, seriesId, doLogin, refetch]);

  return {
    isLikeOn,
    isAlarmOn,
    onClickLikeSeries,
    onClickAlarmOnSeries,
    showArea:
      // 판매 중지의 경우 좋아요한 경우만 영역을 보여줍니다
      (saleState === SaleState.Ing || !!isLikeOn) &&
      // 컨텐츠홈에 완전히 진입한 이후에만 좋아요를 보여줍니다.
      !!isEnteredContentHome,
  };
}
